import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";

const AddCustomerModal = ({ isOpen, onClose, clusters, regions, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    aadhaar_number: "",
    phone_number: "",
    cluster_id: "",
    region_id: "",
    address: "",
    village: "",
    district: "",
    state: "Andhra Pradesh",
    pincode: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const stateOptions = [
    { value: "Andhra Pradesh", label: "Andhra Pradesh" },
    { value: "Telangana", label: "Telangana" },
  ];

  const clusterOptions = [
    { value: "", label: "— No Cluster —" },
    ...(clusters?.map((cluster) => ({
      value: cluster.id,
      label: cluster.name,
    })) || []),
  ];

  const regionOptions =
    regions?.map((region) => ({
      value: region.id,
      label: region.name,
    })) || [];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (
      formData.aadhaar_number &&
      !/^\d{12}$/.test(formData.aadhaar_number.replace(/\s/g, ""))
    ) {
      newErrors.aadhaar_number = "Aadhaar must be 12 digits";
    }

    if (!formData.phone_number?.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone_number.replace(/\s/g, ""))) {
      newErrors.phone_number = "Phone must be 10 digits";
    }

    // cluster_id is optional, no validation needed

    if (!formData.region_id) {
      newErrors.region_id = "Region is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (validateForm()) {
      try {
        setLoading(true);
        // Get project_id from the selected region (region is required)
        const selectedRegion = regions?.find((r) => r.id === formData.region_id);
        if (!selectedRegion) {
          setSubmitError("Selected region not found. Please refresh and try again.");
          setLoading(false);
          return;
        }
        
        const projectId = selectedRegion.project_id;
        if (!projectId) {
          setSubmitError("Selected region does not have a project assigned. Please select a valid region.");
          setLoading(false);
          return;
        }

        // Status will default to 'survey_not_started' in database
        // USN will be auto-generated by database trigger
        await onSave({
          ...formData,
          project_id: projectId,
          status: "survey_not_started", // Explicitly set default status
        });
        handleClose();
      } catch (err) {
        console.error("Error creating customer:", err);
        // Extract meaningful error message
        let errorMessage = "Failed to create customer. Please try again.";
        if (err.message) {
          if (err.message.includes("project_id") && err.message.includes("null")) {
            errorMessage = "Project ID is missing. Please ensure the selected cluster belongs to a project.";
          } else if (err.message.includes("region_id") && err.message.includes("null")) {
            errorMessage = "Region is required. Please select a region.";
          } else if (err.message.includes("cluster_id") && err.message.includes("null")) {
            errorMessage = "Cluster is required. Please select a cluster.";
          } else {
            errorMessage = err.message;
          }
        }
        setSubmitError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      dob: "",
      aadhaar_number: "",
      phone_number: "",
      cluster_id: "",
      region_id: "",
      address: "",
      village: "",
      district: "",
      state: "Andhra Pradesh",
      pincode: "",
    });
    setErrors({});
    setSubmitError(null);
    onClose();
  };

  const handleChange = (field, value) => {
    if (field === "phone_number") {
      value = String(value).replace(/\D/g, "").slice(0, 10);
    } else if (field === "aadhaar_number") {
      value = String(value).replace(/\D/g, "").slice(0, 12);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Add Customer
              </h2>
              <p className="text-sm text-muted-foreground">
                Create a new customer record
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {submitError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{submitError}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={errors.name}
              placeholder="Enter customer name"
            />

            <Input
              label="Date of Birth"
              type="date"
              value={formData.dob}
              onChange={(e) => handleChange("dob", e.target.value)}
              error={errors.dob}
            />

            <Input
              label="Aadhaar Number"
              type="text"
              inputMode="numeric"
              value={formData.aadhaar_number}
              onChange={(e) => handleChange("aadhaar_number", e.target.value)}
              error={errors.aadhaar_number}
              placeholder="12 digit Aadhaar number"
              maxLength={12}
            />

            <Input
              label="Phone Number"
              type="text"
              inputMode="numeric"
              required
              value={formData.phone_number}
              onChange={(e) => handleChange("phone_number", e.target.value)}
              error={errors.phone_number}
              placeholder="10 digit phone number"
              maxLength={10}
            />

            <Select
              label="Cluster (Optional)"
              options={clusterOptions}
              value={formData.cluster_id}
              onChange={(value) => handleChange("cluster_id", value)}
              error={errors.cluster_id}
              placeholder="Select cluster (optional)"
            />

            <Select
              label="Region"
              options={regionOptions}
              value={formData.region_id}
              onChange={(value) => handleChange("region_id", value)}
              error={errors.region_id}
              placeholder="Select region"
              required
            />

            <Input
              label="Address"
              type="text"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              error={errors.address}
              placeholder="Full address"
            />

            <Input
              label="Village"
              type="text"
              value={formData.village}
              onChange={(e) => handleChange("village", e.target.value)}
              error={errors.village}
              placeholder="Village"
            />

            <Input
              label="District"
              type="text"
              value={formData.district}
              onChange={(e) => handleChange("district", e.target.value)}
              error={errors.district}
              placeholder="District"
            />

            <Select
              label="State"
              options={stateOptions}
              value={formData.state}
              onChange={(value) => handleChange("state", value)}
              error={errors.state}
              placeholder="Select state"
            />

            <Input
              label="Pincode"
              type="number"
              value={formData.pincode}
              onChange={(e) => handleChange("pincode", e.target.value)}
              error={errors.pincode}
              placeholder="Pincode"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={loading}
              disabled={loading}
            >
              Create Customer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerModal;
